import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  EmptyState,
  ErrorState,
  FilterField,
  FilterPanel,
  Input,
  LoadingState,
  Page,
  Table,
  Text,
} from "@marekl/adminer/components";
import { useDebouncedSearchFilter, useUrlFilters } from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import { competitionCrumbs } from "../../../shell/CompetitionLayout";
import { useAuth } from "../../../auth/useAuth";
import { canFileEntryForAnyTeam } from "../../../auth/queries";
import { useCompetition } from "../../../lib/queries/competitions";
import { useTeams, type Team } from "../../../lib/queries/teams";

// The manager's step 0 of the entry wizard: a přihláška belongs to a
// team, so the admin picks whose roster to compose from and continues
// into the same fullscreen wizard the leaders use. A page, not a modal
// — the wizard flow has no dialogs.
export function EntryTeamPickPage() {
  const { id } = useParams();
  const compId = id ?? "";
  const { user } = useAuth();
  // Composing an entry needs a roster read and a roster write as well as
  // the competition (canFileEntryForAnyTeam) — a competition-scoped
  // organizer would just collect 403s.
  const canManuallyAdd = canFileEntryForAnyTeam(user);

  const { data: competition, isLoading, error, refetch } = useCompetition(compId);
  const { data: teams = [], isLoading: teamsLoading, error: teamsError, refetch: refetchTeams } =
    useTeams();

  const [searchParams, setSearchParams] = useSearchParams();
  const { setFilter } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page",
  });
  const search = searchParams.get("q") ?? "";
  const { draft: searchDraft, setDraft: setSearchDraft } = useDebouncedSearchFilter({
    value: search,
    onCommit: (next) => setFilter("q", next),
  });

  const crumbs = competitionCrumbs(
    compId,
    competition,
    { label: "Přihlášky", href: `/soutez/${compId}/prihlasky` },
    { label: "Nová přihláška" },
  );

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const visible = teams
    .filter((t) => t.status === "active")
    .filter((t) => !search || t.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, "cs"));

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header title="Nová přihláška" subtitle={competition.name} />
        <Page.Content stack gap="lg">
          {!canManuallyAdd ? (
            <Alert variant="err">
              Ruční přidání přihlášky vyžaduje roli Administrátor nebo Správce členské
              základny; organizátor soutěže nemá přístup k soupiskám týmů.
            </Alert>
          ) : (
            <>
              <Text variant="muted">
                Vyberte tým, za který přihlášku podáváte. Přihláška podaná správcem
                obchází uzávěrku: „po uzávěrce změny provede pořadatel“.
              </Text>
              <FilterPanel>
                <FilterField wide>
                  <Input
                    label="Hledat tým"
                    type="search"
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    placeholder="Název týmu…"
                  />
                </FilterField>
              </FilterPanel>
              <Table<Team>
                framed
                rows={visible}
                rowKey={(t) => t.id}
                isLoading={teamsLoading}
                error={teamsError}
                onRetry={refetchTeams}
                empty={
                  <EmptyState
                    title="Žádný tým"
                    description="Hledání neodpovídá žádný aktivní tým."
                  />
                }
                columns={[
                  {
                    key: "name",
                    header: "Tým",
                    render: (t) => t.name,
                  },
                  {
                    key: "actions",
                    header: "",
                    render: (t) => (
                      <Table.Actions>
                        <Table.Action appearance="button" to={`/teams/${t.id}/souteze/${compId}/nova-prihlaska`}>
                          Vybrat
                        </Table.Action>
                      </Table.Actions>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Page.Content>
      </Page>
    </>
  );
}
